import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import SearchSelect from "./SearchSelect";
import { loadFromSupabase, saveToSupabase } from "../supabaseDB";
import { uploadDataUrlToStorage } from "../storageImages";

/* Dual-write helper. ShipperSettings localStorage is an object keyed by
   supplierId; Supabase `shipper_settings` is one row per supplier
   (PK = supplier_id uuid). On every settings mutation we write the full
   keyed object back to localStorage (existing legacy path) AND fire-and-
   forget a Supabase write for ONLY the shipper that changed. Failures
   are logged but don't block — the localStorage path remains source of
   truth for sync consumers (ShipperPortal reads keyed object directly)
   until those callers migrate. */
function pushShipperSettingsToSupabase(supplierLegacyId, settingsForShipper) {
  if (!supplierLegacyId || !settingsForShipper) return;
  try {
    saveToSupabase("shipper_settings", { id: supplierLegacyId, ...settingsForShipper })
      .catch(function (e) { console.warn("[ShipperSettings] Supabase save failed:", e && e.message); });
  } catch (e) { /* swallow */ }
}

/* ============================================================
   SHIPPER SETTINGS — Arturo-side configuration screen.

   Left column: list of shippers from gfi-suppliers (filtered to
   supplierType === "shipper"). Right column: tabs for per-shipper
   configuration.

   Tabs implemented now:
     - Company Info: GFI-side info the shipper sees in their portal
       (READ-ONLY there). One global record in gfi-consignee-info,
       not per-shipper, since the consignee never changes. Edited
       only from this screen.

   Coming-soon tabs (built in later steps):
     - Authorized Products
     - Customs Brokers (per-shipper assignments)
     - Permissions

   Per-shipper settings live in gfi-shipper-settings keyed by
   supplierId. The shape is seeded on first open of a shipper so
   later steps can write into a stable structure.
   ============================================================ */

const CONSIGNEE_KEY = "gfi-consignee-info";
const SETTINGS_KEY = "gfi-shipper-settings";

/* Defaults seeded the first time the consignee record is opened —
   match the values from CLAUDE.md so Arturo doesn't have to type
   them in. He can edit anything afterwards. */
const DEFAULT_CONSIGNEE = {
  companyName: "Ground Fresh International Inc.",
  addressLine: "152 W Hoffman Ave Ste 5",
  city: "Lindenhurst",
  state: "NY",
  zip: "11757",
  country: "US",
  phone: "",
  email: "",
  website: "",
  organicCertNumber: "PA-OR-1186",
  organicCertifier: "",
  notes: "",
};

/* Per-shipper settings record shape. authorizedProducts / assigned-
   Brokers / permissions are populated by the future tabs; we just
   seed empty containers here. */
const DEFAULT_SHIPPER_SETTINGS = {
  authorizedProducts: [],
  assignedBrokers: [],
  assignedWarehouses: [],  /* FIX 50 — warehouses the shipper may pick. */
  defaultUSBroker: null,
  invoicePrefix: "",
  proformaPrefix: "",
  nextProformaNumber: 1,
  embarquePrefix: "",
  nextEmbarqueNumber: 1,
  permissions: {},
};

const T = {
  es: {
    title: "Configuración del Exportador",
    subtitle: "Define qué ve y qué puede usar cada exportador en su portal.",
    selectShipper: "Selecciona un exportador",
    noShippers: "No hay exportadores registrados. Crea uno en Proveedores primero.",
    shippersCount: "exportador(es)",
    searchShippers: "Buscar exportador...",
    pickPrompt: "Selecciona un exportador a la izquierda para configurar su portal.",
    tabCompany: "Datos del Consignatario", tabProducts: "Productos Autorizados",
    tabBrokers: "Agentes Aduanales", tabWarehouses: "Bodegas", tabInvoicing: "Facturación", tabPermissions: "Permisos",
    invIntro: "Define el prefijo de facturación de este exportador. Aparecerá fijo al inicio del campo de Factura del Exportador en su portal; el exportador solo capturará el número.",
    invPrefix: "Prefijo de Facturación",
    invPrefixHint: "Ejemplo: \"GB-A-\" hace que las facturas se guarden como \"GB-A-5062\".",
    invPreview: "Vista previa en el portal",
    invPreviewHint: "Así verá el campo el exportador al capturar una factura.",
    invSaved: "Prefijo guardado",
    /* Proforma numbering */
    proSection: "Numeración de Proforma",
    proSectionHint: "El portal del exportador auto-genera el consecutivo de cada proforma con este prefijo + número. Después de cada creación, el contador avanza en 1.",
    proPrefix: "Prefijo Proforma",
    proPrefixHint: "Ejemplo: \"GB-\" produce GB-001, GB-002...",
    proNextNumber: "Siguiente Proforma #",
    proNextNumberHint: "Arturo puede resetear, saltar, o iniciar en cualquier número.",
    proPreviewNext: "Siguiente proforma a asignar",
    proResetToInitials: "Usar iniciales del exportador",
    /* Shipment (embarque) numbering */
    embSection: "Numeración de Embarque",
    embSectionHint: "El portal del exportador genera el consecutivo de cada embarque con este prefijo + número. Después de cada creación, el contador avanza en 1.",
    embPrefix: "Prefijo Embarque",
    embPrefixHint: "Ejemplo: \"GB-E\" produce GB-E001, GB-E002...",
    embNextNumber: "Siguiente Embarque #",
    embNextNumberHint: "Arturo puede resetear, saltar, o iniciar en cualquier número (ej: 081).",
    embPreviewNext: "Siguiente embarque a asignar",
    embResetToInitials: "Usar iniciales + E",
    seqWarning: "Cambiar el consecutivo afectará el próximo número generado.",
    saveBtn: "Guardar Cambios", savedToast: "Cambios guardados",
    comingSoon: "Próximamente",
    comingSoonPermissions: "Configura permisos finos del portal: qué módulos, qué acciones.",
    /* Customs Brokers tab */
    cbIntro: "Define qué agente US se fija por default y qué agentes MX puede ver este exportador en su portal. Los cambios se guardan automáticamente.",
    cbSectionUS: "Agente Aduanal US — Default",
    cbSectionUSHint: "Aparece pre-seleccionado y fijo en el portal del exportador.",
    cbSelectUS: "Seleccionar agente US...",
    cbNoUSDefault: "Sin agente US default",
    cbSectionMX: "Agentes Aduanales MX — Asignados",
    cbSectionMXHint: "Marca los agentes que este exportador puede ver y usar.",
    cbOwnedByShipper: "Creados por el exportador (siempre visibles)",
    cbGfiCreated: "Disponibles para asignar",
    cbCreatedBy: "Creado por",
    cbCreatedByShipperShort: "exportador",
    cbCreatedByGfiShort: "GFI",
    cbLockedByShipper: "El exportador es dueño · no se puede desmarcar",
    cbNoMX: "No hay agentes MX registrados aún.",
    cbNoMXOwned: "El exportador no ha creado agentes propios.",
    cbNoMXGfi: "No hay otros agentes MX para asignar.",
    cbSummary: "Resumen",
    cbSummaryUS: "US Default",
    cbSummaryMX: "Agentes MX",
    cbAssignedCount: "{n} asignado(s)",
    cbNoAssigned: "Sin agentes MX asignados.",
    cbAgentMx: "MX", cbAgentUs: "US",
    /* Authorized Products tab */
    apIntro: "Selecciona los productos que este exportador podrá capturar en su portal. Los cambios se guardan automáticamente.",
    apFresh: "Fresco", apFrozen: "Congelado",
    apAuthorizedCount: "{n} producto(s) autorizado(s)",
    apConventional: "Convencional", apOrganic: "Orgánico",
    apNoProducts: "Sin productos en el catálogo. Agrega productos en el módulo Productos primero.",
    apNoForCondition: "Sin productos con esta condición. Cambia el filtro arriba.",
    apExpandAll: "Expandir todo", apCollapseAll: "Colapsar todo",
    apLabelSuffix: "Label",
    /* Company Info form */
    consigneeNote: "Estos datos son globales (compartidos para todos los exportadores). El exportador los ve en SOLO LECTURA en su portal.",
    companyName: "Razón Social / Company Name",
    addressLine: "Dirección", city: "Ciudad", state: "Estado / Estado",
    zip: "Código Postal", country: "País",
    phone: "Teléfono", email: "Email", website: "Sitio Web",
    organicCertNumber: "Certificado USDA Organic #",
    organicCertifier: "Certificador (opcional)",
    notes: "Notas (opcional)",
    save: "Guardar Cambios", saved: "Datos guardados",
    needSomething: "Captura al menos la razón social.",
    /* Preview */
    previewTitle: "Vista previa del exportador",
    previewSub: "Así verá el exportador estos datos en su portal:",
    consignee: "Consignatario",
    organicCert: "Cert. Orgánico USDA",
  },
  en: {
    title: "Shipper Settings",
    subtitle: "Define what each shipper sees and can use in their portal.",
    selectShipper: "Select a shipper",
    noShippers: "No shippers registered. Create one in Suppliers first.",
    shippersCount: "shipper(s)",
    searchShippers: "Search shipper...",
    pickPrompt: "Pick a shipper on the left to configure their portal.",
    tabCompany: "Consignee Info", tabProducts: "Authorized Products",
    tabBrokers: "Customs Brokers", tabWarehouses: "Warehouses", tabInvoicing: "Invoicing", tabPermissions: "Permissions",
    invIntro: "Set the invoice prefix for this shipper. It appears fixed at the start of the Shipper Invoice field in their portal; the shipper only types the number.",
    invPrefix: "Invoice Prefix",
    invPrefixHint: "Example: \"GB-A-\" stores invoices as \"GB-A-5062\".",
    invPreview: "Portal preview",
    invPreviewHint: "This is how the field looks to the shipper when they enter an invoice.",
    invSaved: "Prefix saved",
    proSection: "Proforma Numbering",
    proSectionHint: "The shipper portal auto-generates each proforma's consecutive using this prefix + number. The counter advances by 1 after every creation.",
    proPrefix: "Proforma Prefix",
    proPrefixHint: "Example: \"GB-\" produces GB-001, GB-002...",
    proNextNumber: "Next Proforma #",
    proNextNumberHint: "Arturo can reset, skip, or start at any number.",
    proPreviewNext: "Next proforma to assign",
    proResetToInitials: "Use shipper initials",
    /* Shipment (embarque) numbering */
    embSection: "Shipment Numbering",
    embSectionHint: "The shipper portal generates each shipment's consecutive using this prefix + number. The counter advances by 1 after every creation.",
    embPrefix: "Shipment Prefix",
    embPrefixHint: "Example: \"GB-E\" produces GB-E001, GB-E002...",
    embNextNumber: "Next Shipment #",
    embNextNumberHint: "Arturo can reset, skip, or start at any number (e.g. 081).",
    embPreviewNext: "Next shipment to assign",
    embResetToInitials: "Use initials + E",
    seqWarning: "Changing the sequence will affect the next generated number.",
    saveBtn: "Save Changes", savedToast: "Changes saved",
    comingSoon: "Coming soon",
    comingSoonPermissions: "Fine-grained portal permissions: which modules, which actions.",
    cbIntro: "Define which US broker is pinned by default and which MX brokers this shipper can see in their portal. Changes save automatically.",
    cbSectionUS: "US Customs Broker — Default",
    cbSectionUSHint: "Pre-selected and pinned in the shipper's portal.",
    cbSelectUS: "Select US broker...",
    cbNoUSDefault: "No US default broker",
    cbSectionMX: "MX Customs Brokers — Assigned",
    cbSectionMXHint: "Check the brokers this shipper can see and use.",
    cbOwnedByShipper: "Created by the shipper (always visible)",
    cbGfiCreated: "Available to assign",
    cbCreatedBy: "Created by",
    cbCreatedByShipperShort: "shipper",
    cbCreatedByGfiShort: "GFI",
    cbLockedByShipper: "Shipper-owned · can't uncheck",
    cbNoMX: "No MX brokers registered yet.",
    cbNoMXOwned: "The shipper hasn't created any brokers of their own.",
    cbNoMXGfi: "No other MX brokers available to assign.",
    cbSummary: "Summary",
    cbSummaryUS: "US Default",
    cbSummaryMX: "MX Brokers",
    cbAssignedCount: "{n} assigned",
    cbNoAssigned: "No MX brokers assigned.",
    cbAgentMx: "MX", cbAgentUs: "US",
    apIntro: "Select the products this shipper can capture in their portal. Changes save automatically.",
    apFresh: "Fresh", apFrozen: "Frozen",
    apAuthorizedCount: "{n} product(s) authorized",
    apConventional: "Conventional", apOrganic: "Organic",
    apNoProducts: "No products in the catalog. Add products in the Products registry first.",
    apNoForCondition: "No products with this condition. Switch the filter above.",
    apExpandAll: "Expand all", apCollapseAll: "Collapse all",
    apLabelSuffix: "Label",
    consigneeNote: "These values are global (shared across all shippers). The shipper sees them READ-ONLY in their portal.",
    companyName: "Company Name",
    addressLine: "Address", city: "City", state: "State",
    zip: "ZIP", country: "Country",
    phone: "Phone", email: "Email", website: "Website",
    organicCertNumber: "USDA Organic Certificate #",
    organicCertifier: "Certifier (optional)",
    notes: "Notes (optional)",
    save: "Save Changes", saved: "Saved",
    needSomething: "Enter at least the company name.",
    previewTitle: "Shipper preview",
    previewSub: "This is how the shipper will see these values in their portal:",
    consignee: "Consignee",
    organicCert: "USDA Organic Cert.",
  },
};

/* ---------- Storage helpers ---------- */
function loadLS(k, fb) { try { var d = localStorage.getItem(k); return d ? JSON.parse(d) : fb; } catch { return fb; } }
function saveLS(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); return true; } catch (e) { return false; } }
function loadConsignee() {
  var stored = loadLS(CONSIGNEE_KEY, null);
  if (!stored) return { ...DEFAULT_CONSIGNEE };
  /* Merge so a newer field added to DEFAULT_CONSIGNEE doesn't show
     up as undefined for existing installs. */
  return { ...DEFAULT_CONSIGNEE, ...stored };
}
function loadAllSettings() { return loadLS(SETTINGS_KEY, {}) || {}; }
function loadShippers() {
  var sups = loadLS("gfi-suppliers", []) || [];
  return sups.filter(function (s) { return s.supplierType === "shipper"; });
}
function shipperDisplayName(s) {
  if (!s) return "—";
  return s.shortName || s.fullName || s.name || s.companyName || (s.initials ? "[" + s.initials + "]" : "—");
}

/* ---------- Inline UI primitives ---------- */
const inp = { width: "100%", padding: "7px 9px", border: "1px solid var(--color-border)", borderRadius: 5, background: "var(--color-bg-input)", color: "var(--color-text)", fontSize: 12, fontFamily: "inherit", outline: "none", boxSizing: "border-box" };

function Field({ label, children, full }) {
  return (
    <div style={{ gridColumn: full ? "1 / -1" : "auto" }}>
      <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 3, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</div>
      {children}
    </div>
  );
}
function Toast({ msg, onDismiss }) {
  var [fading, setFading] = useState(false);
  useEffect(function () {
    if (!msg) return;
    setFading(false);
    var fadeT = setTimeout(function () { setFading(true); }, 2700);
    var closeT = setTimeout(onDismiss, 3000);
    return function () { clearTimeout(fadeT); clearTimeout(closeT); };
  }, [msg, onDismiss]);
  if (!msg) return null;
  return createPortal(<div style={{ position: "fixed", bottom: 24, right: 24, padding: "10px 18px", background: "var(--color-accent)", color: "#fff", borderRadius: 8, fontSize: 13, fontWeight: 600, boxShadow: "0 4px 16px rgba(0,0,0,0.2)", zIndex: 999999, animation: fading ? "fadeOutCorner 0.3s ease forwards" : undefined }}>{msg}</div>, document.body);
}

/* =================== TAB: Company Info ===================
   GFI's consignee info — global, not per-shipper. Editing here
   updates every shipper's portal preview at once. */
function CompanyInfoTab({ lang, onToast, onDirtyChange, registerSave }) {
  var t = T[lang];
  var [f, setF] = useState(loadConsignee);
  var [dirty, setDirty] = useState(false);

  var set = function (k, v) { setF(function (prev) { return { ...prev, [k]: v }; }); setDirty(true); };

  /* Save handler — also exposed to the parent via registerSave so the
     header "Save Changes" button can trigger it. Wrapped in useCallback
     so the registration effect doesn't re-fire on every render. */
  var doSave = useCallback(function () {
    if (!f.companyName || !String(f.companyName).trim()) {
      if (onToast) onToast(t.needSomething);
      return;
    }
    saveLS(CONSIGNEE_KEY, f);
    setDirty(false);
    if (onToast) onToast(t.saved);
  }, [f, onToast, t.needSomething, t.saved]);

  /* Push dirty state up so the header button can show enabled/disabled. */
  useEffect(function () {
    if (onDirtyChange) onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  /* Register the save function with the parent. Returns the unregister
     to clean up on unmount or tab change. */
  useEffect(function () {
    if (!registerSave) return undefined;
    return registerSave(doSave);
  }, [registerSave, doSave]);

  /* Build a single-line address for the preview block. Skip blanks
     so partial addresses still read cleanly. */
  var addressOneLine = [f.addressLine, [f.city, f.state, f.zip].filter(Boolean).join(", "), f.country].filter(function (s) { return s && String(s).trim(); }).join(" · ");

  return (
    <div>
      <div style={{ padding: "10px 14px", marginBottom: 18, background: "rgba(59,130,246,0.06)", border: "1px solid rgba(59,130,246,0.2)", borderRadius: 6, fontSize: 12, color: "var(--color-text-secondary)" }}>
        {t.consigneeNote}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
        <Field label={t.companyName}><input value={f.companyName || ""} onChange={function (e) { set("companyName", e.target.value); }} style={inp} /></Field>
        <Field label={t.phone}><input value={f.phone || ""} onChange={function (e) { set("phone", e.target.value); }} style={{ ...inp, fontFamily: "'JetBrains Mono', monospace" }} /></Field>
        <Field label={t.email}><input type="email" value={f.email || ""} onChange={function (e) { set("email", e.target.value); }} style={inp} /></Field>
        <Field label={t.website}><input value={f.website || ""} onChange={function (e) { set("website", e.target.value); }} style={{ ...inp, fontFamily: "'JetBrains Mono', monospace" }} /></Field>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "3fr 2fr 1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
        <Field label={t.addressLine}><input value={f.addressLine || ""} onChange={function (e) { set("addressLine", e.target.value); }} style={inp} /></Field>
        <Field label={t.city}><input value={f.city || ""} onChange={function (e) { set("city", e.target.value); }} style={inp} /></Field>
        <Field label={t.state}><input value={f.state || ""} onChange={function (e) { set("state", e.target.value); }} style={{ ...inp, textAlign: "center", fontFamily: "'JetBrains Mono', monospace", textTransform: "uppercase" }} /></Field>
        <Field label={t.zip}><input value={f.zip || ""} onChange={function (e) { set("zip", e.target.value); }} style={{ ...inp, fontFamily: "'JetBrains Mono', monospace" }} /></Field>
        <Field label={t.country}><input value={f.country || ""} onChange={function (e) { set("country", e.target.value); }} style={inp} /></Field>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 12, marginBottom: 18 }}>
        <Field label={t.organicCertNumber}><input value={f.organicCertNumber || ""} onChange={function (e) { set("organicCertNumber", e.target.value); }} style={{ ...inp, fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "var(--color-accent)" }} /></Field>
        <Field label={t.organicCertifier}><input value={f.organicCertifier || ""} onChange={function (e) { set("organicCertifier", e.target.value); }} style={inp} /></Field>
      </div>

      <Field label={t.notes}>
        <textarea value={f.notes || ""} onChange={function (e) { set("notes", e.target.value); }} rows={2} style={{ ...inp, fontFamily: "inherit", resize: "vertical" }} />
      </Field>

      {/* Save button lives in the parent header bar so it stays visible
          across all tabs. */}

      {/* Shipper preview — same layout the portal will use to render
          the consignee block. Mirrors a header card on a Sales Report
          or Carta Porte: company name big, address smaller, cert
          number highlighted. */}
      <div style={{ marginTop: 26 }}>
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--color-accent)", marginBottom: 8, paddingBottom: 4, borderBottom: "1px solid var(--color-border)" }}>{t.previewTitle}</div>
        <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginBottom: 8 }}>{t.previewSub}</div>
        <div style={{ padding: "16px 20px", background: "var(--color-bg)", border: "1px solid var(--color-border)", borderRadius: 8 }}>
          <div style={{ fontSize: 10, color: "var(--color-text-muted)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>{t.consignee}</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: "var(--color-text)", marginBottom: 4 }}>{f.companyName || "—"}</div>
          {addressOneLine && <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 4 }}>{addressOneLine}</div>}
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 11, color: "var(--color-text-secondary)", marginTop: 8 }}>
            {f.phone && <span><span style={{ color: "var(--color-text-muted)" }}>Tel:</span> <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{f.phone}</span></span>}
            {f.email && <span><span style={{ color: "var(--color-text-muted)" }}>Email:</span> <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{f.email}</span></span>}
            {f.website && <span><span style={{ color: "var(--color-text-muted)" }}>Web:</span> <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{f.website}</span></span>}
          </div>
          {f.organicCertNumber && (
            <div style={{ marginTop: 12, padding: "6px 10px", display: "inline-block", background: "rgba(42,122,111,0.08)", border: "1px solid rgba(42,122,111,0.25)", borderRadius: 5 }}>
              <span style={{ fontSize: 9, color: "var(--color-text-muted)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginRight: 8 }}>{t.organicCert}</span>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "var(--color-accent)", fontSize: 13 }}>{f.organicCertNumber}</span>
              {f.organicCertifier && <span style={{ marginLeft: 8, color: "var(--color-text-muted)", fontSize: 10 }}>· {f.organicCertifier}</span>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* =================== TAB: Authorized Products ===================
   Picker grouped by condition (Fresh/Frozen toggle) → product base
   (collapsible) → category (Conventional left / Organic right) →
   size → label checkbox. Tri-state checkboxes at the product and
   size level let Arturo bulk-select. Writes to gfi-shipper-settings
   under the supplierId, field authorizedProducts (array of product
   IDs) on every change — no save button. */

/* Numeric-prefix-aware size sort so "6oz" < "12oz" < "18oz". Falls
   back to string compare for non-numeric sizes ("1pt", "Other"). */
function sortSizes(a, b) {
  var na = parseFloat(a), nb = parseFloat(b);
  var aHas = !isNaN(na), bHas = !isNaN(nb);
  if (aHas && bHas && na !== nb) return na - nb;
  if (aHas && !bHas) return -1;
  if (!aHas && bHas) return 1;
  return String(a).localeCompare(String(b));
}

/* Native checkbox doesn't accept `indeterminate` as a prop; the only
   way to set it is via DOM ref. Wrapping this once means callers
   don't repeat the ref boilerplate. */
function TriCheckbox({ checked, indeterminate, onChange, style }) {
  return (
    <input
      type="checkbox"
      checked={!!checked}
      ref={function (el) { if (el) el.indeterminate = !checked && !!indeterminate; }}
      onChange={onChange}
      onClick={function (e) { e.stopPropagation(); }}
      style={{ accentColor: "var(--color-accent)", cursor: "pointer", width: 15, height: 15, ...(style || {}) }}
    />
  );
}

function AuthorizedProductsTab({ supplierId, lang, onToast, registerSave, onDirtyChange }) {
  var t = T[lang];
  var [condition, setCondition] = useState("Fresh");
  var [expanded, setExpanded] = useState({});
  var [authorized, setAuthorized] = useState({}); /* { productId: true } */
  var [allProducts, setAllProducts] = useState([]);
  /* Dirty flag activates the header "Save Changes" button even though
     this tab auto-saves on every toggle. The header save just calls
     back into this tab via registerSave; we then clear the flag.
     Without this, the button never lit up for product toggles. */
  var [dirty, setDirty] = useState(false);

  /* Load the catalog + this shipper's prior authorizations whenever
     the selected shipper changes. Same cache-warm pattern as
     BrokersTab so any future FK on products in shipper_settings
     resolves correctly on save. */
  useEffect(function () {
    var initial = loadLS("gfi-products", []);
    setAllProducts(initial);
    loadFromSupabase("products")
      .then(function (rows) { setAllProducts(rows || initial); })
      .catch(function () {});
    var all = loadAllSettings();
    var rec = all[supplierId] || {};
    var ids = {};
    (rec.authorizedProducts || []).forEach(function (id) { ids[String(id)] = true; });
    setAuthorized(ids);
    /* Reset expanded state when switching shippers so the new shipper
       opens to a fresh, collapsed view. */
    setExpanded({});
    setDirty(false);
  }, [supplierId]);

  useEffect(function () { if (onDirtyChange) onDirtyChange(dirty); }, [dirty, onDirtyChange]);

  /* Register a save callback. Storage is already current (we
     persist on every change); the header click just clears the
     dirty indicator. */
  useEffect(function () {
    if (!registerSave) return;
    var unregister = registerSave(function () { setDirty(false); });
    return unregister;
  }, [registerSave]);

  /* Persist the authorized set back to gfi-shipper-settings + Supabase.
     Seeds the per-shipper record with DEFAULT_SHIPPER_SETTINGS if
     missing so future fields land in the right shape. */
  function persist(nextAuthorized) {
    var all = loadAllSettings();
    if (!all[supplierId]) all[supplierId] = { ...DEFAULT_SHIPPER_SETTINGS };
    all[supplierId] = { ...all[supplierId], authorizedProducts: Object.keys(nextAuthorized).filter(function (k) { return nextAuthorized[k]; }) };
    saveLS(SETTINGS_KEY, all);
    pushShipperSettingsToSupabase(supplierId, all[supplierId]);
    setDirty(true);
  }

  /* Toggle helpers: every mutation computes the next state, then
     calls setAuthorized + persist in lockstep so the UI and storage
     never drift. */
  function toggleOne(id) {
    var key = String(id);
    var next = { ...authorized };
    if (next[key]) delete next[key]; else next[key] = true;
    setAuthorized(next); persist(next);
  }
  function toggleMany(ids) {
    var keys = ids.map(String);
    var next = { ...authorized };
    var allSelected = keys.every(function (k) { return next[k]; });
    if (allSelected) keys.forEach(function (k) { delete next[k]; });
    else keys.forEach(function (k) { next[k] = true; });
    setAuthorized(next); persist(next);
  }

  /* Filter by the active condition tab. Treat "Fresh Cut" as Fresh
     since the spec toggle is only two states; legacy products with
     no condition default to Fresh. Inactive catalog entries
     (isActive === false) stay out of the picker. */
  var filteredProducts = useMemo(function () {
    return allProducts.filter(function (p) {
      if (p.isActive === false) return false;
      var cond = p.condition || "Fresh";
      if (condition === "Fresh") return cond === "Fresh" || cond === "Fresh Cut";
      return cond === "Frozen";
    });
  }, [allProducts, condition]);

  /* Group structure: { productBase: { CV: { size: [products] }, OG: { size: [products] } } }. */
  var grouped = useMemo(function () {
    var out = {};
    filteredProducts.forEach(function (p) {
      var base = p.product || p.productName || "Other";
      var cat = (p.category === "Organic" || p.category === "OG") ? "OG" : "CV";
      var size = p.size || "—";
      if (!out[base]) out[base] = { CV: {}, OG: {} };
      if (!out[base][cat][size]) out[base][cat][size] = [];
      out[base][cat][size].push(p);
    });
    /* Sort labels within each size for deterministic rendering. */
    Object.keys(out).forEach(function (base) {
      ["CV", "OG"].forEach(function (cat) {
        Object.keys(out[base][cat]).forEach(function (size) {
          out[base][cat][size].sort(function (a, b) { return String(a.labelInitials || "").localeCompare(String(b.labelInitials || "")); });
        });
      });
    });
    return out;
  }, [filteredProducts]);

  var baseProducts = useMemo(function () { return Object.keys(grouped).sort(); }, [grouped]);

  /* Authorized count is GLOBAL (counts across all conditions, not
     just the active filter), so toggling Fresh↔Frozen doesn't make
     the badge jump misleadingly. */
  var authorizedCount = useMemo(function () {
    var n = 0;
    Object.keys(authorized).forEach(function (k) { if (authorized[k]) n += 1; });
    return n;
  }, [authorized]);

  /* Status helpers for tri-state checkboxes. */
  function idsFlat(arrs) {
    /* Flatten an array of arrays of products → array of string ids. */
    var out = [];
    arrs.forEach(function (arr) { arr.forEach(function (p) { out.push(String(p.id)); }); });
    return out;
  }
  function statusOf(ids) {
    if (ids.length === 0) return { checked: false, indeterminate: false };
    var sel = 0;
    for (var i = 0; i < ids.length; i++) if (authorized[ids[i]]) sel++;
    if (sel === 0) return { checked: false, indeterminate: false };
    if (sel === ids.length) return { checked: true, indeterminate: false };
    return { checked: false, indeterminate: true };
  }

  function toggleProduct(base) {
    setExpanded(function (prev) { return { ...prev, [base]: !prev[base] }; });
  }
  function expandAll() {
    var next = {}; baseProducts.forEach(function (b) { next[b] = true; }); setExpanded(next);
  }
  function collapseAll() { setExpanded({}); }

  /* ---------- Render ---------- */

  var conditionPill = function (key, label) {
    var active = condition === key;
    return (
      <button key={key} onClick={function () { setCondition(key); }} style={{
        padding: "8px 18px", border: "1px solid " + (active ? "var(--color-accent)" : "var(--color-border)"),
        background: active ? "var(--color-accent)" : "transparent",
        color: active ? "#fff" : "var(--color-text-secondary)",
        cursor: "pointer", fontSize: 12, fontWeight: 700, borderRadius: 0,
      }}>{label}</button>
    );
  };

  if (allProducts.length === 0) {
    return (
      <div style={{ padding: "60px 30px", textAlign: "center", color: "var(--color-text-muted)", fontSize: 13 }}>
        {t.apNoProducts}
      </div>
    );
  }

  return (
    <div>
      <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 14 }}>{t.apIntro}</div>

      {/* Toolbar: condition toggle + counter + expand/collapse */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
        <div style={{ display: "inline-flex", borderRadius: 6, overflow: "hidden", border: "1px solid var(--color-border)" }}>
          {conditionPill("Fresh", t.apFresh)}
          {conditionPill("Frozen", t.apFrozen)}
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <span style={{ padding: "6px 12px", borderRadius: 14, background: "rgba(42,122,111,0.08)", border: "1px solid rgba(42,122,111,0.25)", fontSize: 11, fontWeight: 700, color: "var(--color-accent)" }}>
            {t.apAuthorizedCount.replace("{n}", authorizedCount)}
          </span>
          <button onClick={expandAll} style={{ padding: "6px 10px", borderRadius: 4, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11 }}>{t.apExpandAll}</button>
          <button onClick={collapseAll} style={{ padding: "6px 10px", borderRadius: 4, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11 }}>{t.apCollapseAll}</button>
        </div>
      </div>

      {/* Empty state for the active condition only. */}
      {baseProducts.length === 0 && (
        <div style={{ padding: "30px 20px", textAlign: "center", color: "var(--color-text-muted)", fontSize: 12, border: "1px dashed var(--color-border)", borderRadius: 8 }}>{t.apNoForCondition}</div>
      )}

      {/* Product base accordions — scroll inside this list so
          expanding everything stays usable on long catalogs. */}
      <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: "calc(100vh - 360px)", overflowY: "auto", paddingRight: 4 }}>
        {baseProducts.map(function (base) {
          var node = grouped[base];
          var cvSizes = Object.keys(node.CV).sort(sortSizes);
          var ogSizes = Object.keys(node.OG).sort(sortSizes);
          var allIds = idsFlat([].concat(
            cvSizes.map(function (s) { return node.CV[s]; }),
            ogSizes.map(function (s) { return node.OG[s]; })
          ));
          var st = statusOf(allIds);
          var isExpanded = !!expanded[base];

          return (
            <div key={base} style={{ border: "1px solid var(--color-border)", borderRadius: 6, overflow: "hidden", background: "var(--color-bg-card)" }}>
              {/* Header — click anywhere to expand; checkbox toggles all */}
              <div onClick={function () { toggleProduct(base); }} style={{
                padding: "10px 14px", display: "flex", alignItems: "center", gap: 10,
                cursor: "pointer", background: isExpanded ? "var(--color-bg)" : "transparent",
                borderBottom: isExpanded ? "1px solid var(--color-border)" : "none",
              }}>
                <span style={{ fontSize: 11, color: "var(--color-text-muted)", fontFamily: "'JetBrains Mono', monospace", width: 14, display: "inline-block" }}>{isExpanded ? "▼" : "▶"}</span>
                <TriCheckbox checked={st.checked} indeterminate={st.indeterminate} onChange={function () { toggleMany(allIds); }} />
                <span style={{ fontSize: 13, fontWeight: 700, color: "var(--color-text)" }}>{base}</span>
                <span style={{ fontSize: 10, color: "var(--color-text-muted)", marginLeft: "auto" }}>{allIds.filter(function (id) { return authorized[id]; }).length} / {allIds.length}</span>
              </div>

              {isExpanded && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", borderTop: "0" }}>
                  {/* Conventional column */}
                  <div style={{ padding: "10px 14px", borderRight: "1px solid var(--color-border)" }}>
                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--color-text-muted)", marginBottom: 8, paddingBottom: 4, borderBottom: "1px solid var(--color-border)" }}>{t.apConventional}</div>
                    {cvSizes.length === 0 ? <div style={{ fontSize: 11, color: "var(--color-text-muted)", fontStyle: "italic" }}>—</div> : cvSizes.map(function (size) {
                      var arr = node.CV[size];
                      var sizeIds = arr.map(function (p) { return String(p.id); });
                      var sst = statusOf(sizeIds);
                      return (
                        <div key={"cv-" + size} style={{ marginBottom: 10 }}>
                          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontWeight: 600, fontSize: 12 }}>
                            <TriCheckbox checked={sst.checked} indeterminate={sst.indeterminate} onChange={function () { toggleMany(sizeIds); }} />
                            <span>{size}</span>
                          </label>
                          <div style={{ paddingLeft: 24, marginTop: 4, display: "flex", flexDirection: "column", gap: 3 }}>
                            {arr.map(function (p) {
                              var key = String(p.id);
                              return (
                                <label key={key} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 11, color: "var(--color-text-secondary)" }}>
                                  <input type="checkbox" checked={!!authorized[key]} onChange={function () { toggleOne(key); }} style={{ accentColor: "var(--color-accent)", cursor: "pointer", width: 13, height: 13 }} />
                                  <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{p.labelInitials || "—"}</span>
                                  <span style={{ color: "var(--color-text-muted)" }}>{t.apLabelSuffix}</span>
                                  {p.qualityGrade && <span style={{ padding: "1px 6px", borderRadius: 4, fontSize: 9, fontWeight: 700, background: p.qualityGrade === "Premium" ? "rgba(142,68,173,0.10)" : "rgba(243,156,18,0.10)", color: p.qualityGrade === "Premium" ? "#7D3C98" : "#B7950B" }}>{p.qualityGrade}</span>}
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Organic column */}
                  <div style={{ padding: "10px 14px" }}>
                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--color-accent)", marginBottom: 8, paddingBottom: 4, borderBottom: "1px solid var(--color-border)" }}>{t.apOrganic}</div>
                    {ogSizes.length === 0 ? <div style={{ fontSize: 11, color: "var(--color-text-muted)", fontStyle: "italic" }}>—</div> : ogSizes.map(function (size) {
                      var arr = node.OG[size];
                      var sizeIds = arr.map(function (p) { return String(p.id); });
                      var sst = statusOf(sizeIds);
                      return (
                        <div key={"og-" + size} style={{ marginBottom: 10 }}>
                          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontWeight: 600, fontSize: 12 }}>
                            <TriCheckbox checked={sst.checked} indeterminate={sst.indeterminate} onChange={function () { toggleMany(sizeIds); }} />
                            <span>{size}</span>
                          </label>
                          <div style={{ paddingLeft: 24, marginTop: 4, display: "flex", flexDirection: "column", gap: 3 }}>
                            {arr.map(function (p) {
                              var key = String(p.id);
                              return (
                                <label key={key} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 11, color: "var(--color-text-secondary)" }}>
                                  <input type="checkbox" checked={!!authorized[key]} onChange={function () { toggleOne(key); }} style={{ accentColor: "var(--color-accent)", cursor: "pointer", width: 13, height: 13 }} />
                                  <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{p.labelInitials || "—"}</span>
                                  <span style={{ color: "var(--color-text-muted)" }}>{t.apLabelSuffix}</span>
                                  {p.qualityGrade && <span style={{ padding: "1px 6px", borderRadius: 4, fontSize: 9, fontWeight: 700, background: p.qualityGrade === "Premium" ? "rgba(142,68,173,0.10)" : "rgba(243,156,18,0.10)", color: p.qualityGrade === "Premium" ? "#7D3C98" : "#B7950B" }}>{p.qualityGrade}</span>}
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* =================== TAB: Customs Brokers ===================
   Two configuration sections + a summary:
     A. US Default broker — single SearchSelect across all brokers,
        labeled with the agent's country tag. Pinned read-only in
        the shipper's portal.
     B. MX Brokers Assigned — checkboxes split into two lists:
        shipper-owned (always visible, locked) and Arturo-owned
        (toggleable). The "effective" assigned set the shipper
        actually sees in their portal is shipper-owned ∪ assigned.
     C. Summary — quick reference card for Arturo. */

function brokerDisplayName(b) {
  return b.shortName || b.fullName || (b.initials ? "[" + b.initials + "]" : "—");
}
function brokerLocation(b) {
  return [b.city, b.country].filter(Boolean).join(", ");
}

function BrokersTab({ supplierId, lang, onToast, registerSave, onDirtyChange }) {
  var t = T[lang];
  var [brokers, setBrokers] = useState([]);
  var [defaultUS, setDefaultUS] = useState(null); /* broker id or null */
  var [assigned, setAssigned] = useState({});     /* { brokerId: true } */
  /* See AuthorizedProductsTab — auto-save tab that still surfaces a
     dirty signal to the header for visual feedback. */
  var [dirty, setDirty] = useState(false);

  /* Reload everything when the selected shipper changes.
     IMPORTANT: also warm the supabaseDB in-memory cache for brokers
     so the FK resolver in saveToSupabase("shipper_settings", ...)
     can translate the broker's legacy id → uuid for the
     default_us_broker_id column. Without this the uuid column was
     written as null because the cache was empty on first save.
     loadFromSupabase populates `cache.get("brokers")`. */
  useEffect(function () {
    var initial = loadLS("gfi-brokers", []);
    setBrokers(initial);
    loadFromSupabase("brokers")
      .then(function (rows) { setBrokers(rows || initial); })
      .catch(function () { /* keep the localStorage fallback */ });
    var all = loadAllSettings();
    var rec = all[supplierId] || {};
    setDefaultUS(rec.defaultUSBroker || null);
    var map = {};
    (rec.assignedBrokers || []).forEach(function (id) { map[String(id)] = true; });
    setAssigned(map);
    setDirty(false);
  }, [supplierId]);

  useEffect(function () { if (onDirtyChange) onDirtyChange(dirty); }, [dirty, onDirtyChange]);
  useEffect(function () {
    if (!registerSave) return;
    var unregister = registerSave(function () { setDirty(false); });
    return unregister;
  }, [registerSave]);

  /* Persist helpers — write the full shipper record back so any
     other fields (authorizedProducts, etc.) survive untouched.
     Dual-write to Supabase for the changed shipper's row. */
  function persist(patch) {
    var all = loadAllSettings();
    if (!all[supplierId]) all[supplierId] = { ...DEFAULT_SHIPPER_SETTINGS };
    all[supplierId] = { ...all[supplierId], ...patch };
    saveLS(SETTINGS_KEY, all);
    pushShipperSettingsToSupabase(supplierId, all[supplierId]);
    setDirty(true);
  }
  function setDefaultUSAndPersist(brokerId) {
    var v = brokerId || null;
    setDefaultUS(v);
    persist({ defaultUSBroker: v });
  }
  function toggleAssigned(brokerId) {
    var k = String(brokerId);
    var next = { ...assigned };
    if (next[k]) delete next[k]; else next[k] = true;
    setAssigned(next);
    persist({ assignedBrokers: Object.keys(next).filter(function (x) { return next[x]; }) });
  }

  /* Categorize brokers. Shipper-owned MX brokers are auto-included
     in the assigned set the portal reads — they don't need to live
     in assignedBrokers (and showing them as toggleable would be
     misleading since the shipper owns them).
     US Default dropdown shows ALL brokers regardless of agentType
     so Arturo can pick whichever the shipper actually uses — per
     spec we don't filter by country. */
  var usOptions = useMemo(function () {
    return brokers.map(function (b) {
      var tag = b.agentType === "american" ? t.cbAgentUs : (b.agentType === "mexican" ? t.cbAgentMx : "");
      /* Use the Supabase UUID (b._uuid) as the option value when
         present so the SearchSelect matches the value stored in
         shipper_settings.default_us_broker_id (also a UUID). Falls
         back to legacy b.id for brokers that haven't been migrated
         yet — keeps the dropdown working on partially-migrated data. */
      return { value: b._uuid || b.id, label: brokerDisplayName(b) + (tag ? " · " + tag : "") };
    });
  }, [brokers, t]);
  var mxAll = useMemo(function () { return brokers.filter(function (b) { return b.agentType === "mexican"; }); }, [brokers]);
  var mxOwnedByShipper = useMemo(function () { return mxAll.filter(function (b) { return String(b.createdBy) === String(supplierId); }); }, [mxAll, supplierId]);
  var mxOthers = useMemo(function () { return mxAll.filter(function (b) { return String(b.createdBy) !== String(supplierId); }); }, [mxAll, supplierId]);

  /* Effective list the shipper sees = shipper-owned ∪ assigned.
     Dedupe by broker id; preserve order (owned first). */
  var effectiveBrokers = useMemo(function () {
    var seen = {};
    var out = [];
    mxOwnedByShipper.forEach(function (b) { var k = String(b.id); if (!seen[k]) { seen[k] = true; out.push(b); } });
    mxOthers.forEach(function (b) { var k = String(b.id); if (!seen[k] && assigned[k]) { seen[k] = true; out.push(b); } });
    return out;
  }, [mxOwnedByShipper, mxOthers, assigned]);

  var currentDefaultUS = useMemo(function () {
    if (!defaultUS) return null;
    /* `defaultUS` arrives from shipper_settings as a UUID (rowToRecord
       maps default_us_broker_id straight onto the record), but the
       brokers catalog rows expose the legacy id as `b.id` and the
       UUID as `b._uuid`. Match either so the dropdown's "current
       selection" panel pre-renders regardless of which id shape was
       persisted. */
    var target = String(defaultUS);
    return brokers.find(function (b) { return String(b.id) === target || String(b._uuid) === target; }) || null;
  }, [defaultUS, brokers]);

  /* ---------- Render ---------- */
  return (
    <div>
      <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 14 }}>{t.cbIntro}</div>

      {/* ===== SECTION A — US DEFAULT ===== */}
      <div style={{ marginBottom: 22, padding: "14px 16px", border: "1px solid var(--color-border)", borderRadius: 8, background: "var(--color-bg-card)" }}>
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--color-accent)", marginBottom: 4 }}>{t.cbSectionUS}</div>
        <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 10 }}>{t.cbSectionUSHint}</div>
        <div style={{ maxWidth: 380 }}>
          <SearchSelect value={defaultUS || ""} onChange={function (v) { setDefaultUSAndPersist(v); }} placeholder={t.cbSelectUS} options={usOptions} />
        </div>
      </div>

      {/* ===== SECTION B — MX BROKERS ===== */}
      <div style={{ marginBottom: 22, padding: "14px 16px", border: "1px solid var(--color-border)", borderRadius: 8, background: "var(--color-bg-card)" }}>
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--color-accent)", marginBottom: 4 }}>{t.cbSectionMX}</div>
        <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 12 }}>{t.cbSectionMXHint}</div>

        {mxAll.length === 0 ? (
          <div style={{ padding: "20px", textAlign: "center", color: "var(--color-text-muted)", fontSize: 12, border: "1px dashed var(--color-border)", borderRadius: 6 }}>{t.cbNoMX}</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>

            {/* Shipper-owned column */}
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-muted)", marginBottom: 6, paddingBottom: 4, borderBottom: "1px solid var(--color-border)" }}>{t.cbOwnedByShipper}</div>
              {mxOwnedByShipper.length === 0 ? (
                <div style={{ fontSize: 11, color: "var(--color-text-muted)", fontStyle: "italic", padding: "8px 4px" }}>{t.cbNoMXOwned}</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {mxOwnedByShipper.map(function (b) {
                    return (
                      <div key={b.id} title={t.cbLockedByShipper} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", background: "rgba(42,122,111,0.05)", border: "1px solid rgba(42,122,111,0.2)", borderRadius: 5 }}>
                        <input type="checkbox" checked readOnly disabled style={{ accentColor: "var(--color-accent)", cursor: "not-allowed", width: 14, height: 14 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: "var(--color-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{brokerDisplayName(b)}</div>
                          {(b.fullName && b.fullName !== brokerDisplayName(b)) && <div style={{ fontSize: 10, color: "var(--color-text-muted)" }}>{b.fullName}</div>}
                        </div>
                        <span style={{ padding: "1px 6px", borderRadius: 3, fontSize: 9, fontWeight: 700, background: "rgba(42,122,111,0.15)", color: "var(--color-accent)", textTransform: "uppercase", letterSpacing: "0.04em" }}>🔒 {t.cbCreatedByShipperShort}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Available column */}
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-muted)", marginBottom: 6, paddingBottom: 4, borderBottom: "1px solid var(--color-border)" }}>{t.cbGfiCreated}</div>
              {mxOthers.length === 0 ? (
                <div style={{ fontSize: 11, color: "var(--color-text-muted)", fontStyle: "italic", padding: "8px 4px" }}>{t.cbNoMXGfi}</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {mxOthers.map(function (b) {
                    var k = String(b.id);
                    var checked = !!assigned[k];
                    return (
                      <label key={k} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", border: "1px solid var(--color-border)", borderRadius: 5, cursor: "pointer", background: checked ? "rgba(59,130,246,0.05)" : "transparent" }}>
                        <input type="checkbox" checked={checked} onChange={function () { toggleAssigned(k); }} style={{ accentColor: "var(--color-accent)", cursor: "pointer", width: 14, height: 14 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: "var(--color-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{brokerDisplayName(b)}</div>
                          {(b.fullName && b.fullName !== brokerDisplayName(b)) && <div style={{ fontSize: 10, color: "var(--color-text-muted)" }}>{b.fullName}</div>}
                        </div>
                        <span style={{ padding: "1px 6px", borderRadius: 3, fontSize: 9, fontWeight: 700, background: "rgba(107,114,128,0.12)", color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.04em" }}>{t.cbCreatedByGfiShort}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ===== SECTION C — SUMMARY ===== */}
      <div style={{ padding: "14px 16px", border: "1px solid var(--color-border)", borderRadius: 8, background: "var(--color-bg)" }}>
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--color-accent)", marginBottom: 10 }}>{t.cbSummary}</div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 9, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>{t.cbSummaryUS}</div>
            <div style={{ marginTop: 4, fontSize: 13, fontWeight: 700, color: currentDefaultUS ? "var(--color-text)" : "var(--color-text-muted)" }}>
              {currentDefaultUS ? brokerDisplayName(currentDefaultUS) : t.cbNoUSDefault}
            </div>
            {currentDefaultUS && brokerLocation(currentDefaultUS) && <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginTop: 2 }}>{brokerLocation(currentDefaultUS)}</div>}
          </div>
          <div>
            <div style={{ fontSize: 9, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>{t.cbSummaryMX}</div>
            <div style={{ marginTop: 4, fontSize: 13, fontWeight: 700, color: effectiveBrokers.length > 0 ? "var(--color-text)" : "var(--color-text-muted)" }}>
              {t.cbAssignedCount.replace("{n}", effectiveBrokers.length)}
            </div>
          </div>
        </div>

        {/* Visual list of effective MX brokers — what the shipper
            will actually see in their portal dropdown. */}
        {effectiveBrokers.length === 0 ? (
          <div style={{ padding: "12px", fontSize: 11, color: "var(--color-text-muted)", fontStyle: "italic", textAlign: "center", border: "1px dashed var(--color-border)", borderRadius: 5 }}>{t.cbNoAssigned}</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 6 }}>
            {effectiveBrokers.map(function (b) {
              var ownedByShipper = String(b.createdBy) === String(supplierId);
              return (
                <div key={b.id} style={{ padding: "8px 10px", background: "var(--color-bg-card)", border: "1px solid var(--color-border)", borderRadius: 5, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6 }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "var(--color-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{brokerDisplayName(b)}</div>
                    {b.fullName && b.fullName !== brokerDisplayName(b) && <div style={{ fontSize: 10, color: "var(--color-text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{b.fullName}</div>}
                  </div>
                  <span style={{ padding: "1px 6px", borderRadius: 3, fontSize: 9, fontWeight: 700, background: ownedByShipper ? "rgba(42,122,111,0.12)" : "rgba(107,114,128,0.12)", color: ownedByShipper ? "var(--color-accent)" : "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap" }}>
                    {ownedByShipper ? t.cbCreatedByShipperShort : t.cbCreatedByGfiShort}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/* =================== TAB: Invoicing ===================
   Per-shipper invoice prefix. Saved live (no save button) into
   gfi-shipper-settings[supplierId].invoicePrefix. The Shipper
   Portal reads it on render and renders it as a non-editable
   leading label next to the Shipper Invoice number input. */
function InvoicingTab({ supplierId, lang, onToast, onDirtyChange, registerSave }) {
  var t = T[lang];
  var [prefix, setPrefix] = useState("");
  var [proPrefix, setProPrefix] = useState("");
  var [nextNum, setNextNum] = useState("1");
  var [embPrefix, setEmbPrefix] = useState("");
  var [nextEmb, setNextEmb] = useState("1");
  var [shipperInitials, setShipperInitials] = useState("");
  /* Dirty tracked alongside auto-save: fields persist on each keystroke
     (so a partial edit isn't lost on tab change), but the explicit
     "Save Changes" button in the header still surfaces a confirmation
     toast and runs the integer normalization on next-number fields. */
  var [dirty, setDirty] = useState(false);

  useEffect(function () {
    var all = loadAllSettings();
    var rec = all[supplierId] || {};
    setPrefix(rec.invoicePrefix || "");
    setProPrefix(rec.proformaPrefix || "");
    setNextNum(String(rec.nextProformaNumber != null ? rec.nextProformaNumber : 1));
    setEmbPrefix(rec.embarquePrefix || "");
    setNextEmb(String(rec.nextEmbarqueNumber != null ? rec.nextEmbarqueNumber : 1));
    /* Pull initials from the supplier record so we can offer them
       as a one-click default for the proforma prefix. */
    var sup = loadLS("gfi-suppliers", []).find(function (s) { return String(s.id) === String(supplierId); });
    setShipperInitials(sup ? String(sup.initials || sup.shortName || "").toUpperCase() : "");
    setDirty(false); /* freshly loaded shipper has no pending changes */
  }, [supplierId]);

  function persistPatch(patch) {
    var all = loadAllSettings();
    if (!all[supplierId]) all[supplierId] = { ...DEFAULT_SHIPPER_SETTINGS };
    all[supplierId] = { ...all[supplierId], ...patch };
    saveLS(SETTINGS_KEY, all);
    pushShipperSettingsToSupabase(supplierId, all[supplierId]);
  }
  function onPrefixChange(v) { setPrefix(v); persistPatch({ invoicePrefix: v }); setDirty(true); }
  function onProPrefixChange(v) { setProPrefix(v); persistPatch({ proformaPrefix: v }); setDirty(true); }
  function onNextNumChange(v) {
    /* Strip non-digits so the input always stores a clean integer
       string; persist as a Number so reads don't have to parse. */
    var digits = String(v).replace(/\D/g, "");
    setNextNum(digits);
    var n = parseInt(digits, 10);
    persistPatch({ nextProformaNumber: isNaN(n) ? 1 : n });
    setDirty(true);
  }
  function applyInitialsToProPrefix() {
    if (!shipperInitials) return;
    var guess = shipperInitials + "-";
    setProPrefix(guess); persistPatch({ proformaPrefix: guess }); setDirty(true);
  }
  function onEmbPrefixChange(v) { setEmbPrefix(v); persistPatch({ embarquePrefix: v }); setDirty(true); }
  function onNextEmbChange(v) {
    var digits = String(v).replace(/\D/g, "");
    setNextEmb(digits);
    var n = parseInt(digits, 10);
    persistPatch({ nextEmbarqueNumber: isNaN(n) ? 1 : n });
    setDirty(true);
  }
  function applyInitialsToEmbPrefix() {
    if (!shipperInitials) return;
    var guess = shipperInitials + "-E";
    setEmbPrefix(guess); persistPatch({ embarquePrefix: guess }); setDirty(true);
  }
  /* Explicit Save — re-persists EVERY field at once with the
     current state values and surfaces a toast so the user has
     visual confirmation. Auto-save on each keystroke already
     fires via persistPatch above; this is a belt-and-suspenders
     manual trigger that also normalizes nextProformaNumber to a
     real integer (the on-change handler stores it as a digit
     string while typing). Triggered by the header bar's "Save
     Changes" button via registerSave below. */
  var handleSaveAll = useCallback(function () {
    var n = parseInt(String(nextNum).replace(/\D/g, ""), 10);
    var ne = parseInt(String(nextEmb).replace(/\D/g, ""), 10);
    persistPatch({
      invoicePrefix: prefix,
      proformaPrefix: proPrefix,
      nextProformaNumber: isNaN(n) ? 1 : n,
      embarquePrefix: embPrefix,
      nextEmbarqueNumber: isNaN(ne) ? 1 : ne,
    });
    setDirty(false);
    if (onToast) onToast(t.savedToast);
    // persistPatch is defined locally and references supplierId; lint
    // can't see the closure so we suppress.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefix, proPrefix, nextNum, embPrefix, nextEmb, supplierId, onToast, t.savedToast]);

  useEffect(function () {
    if (onDirtyChange) onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  useEffect(function () {
    if (!registerSave) return undefined;
    return registerSave(handleSaveAll);
  }, [registerSave, handleSaveAll]);

  /* Compose the preview consec the same way ShipperPortal does, so
     Arturo sees the exact string the next proforma will get. */
  var proPreview = (function () {
    var n = parseInt(nextNum, 10);
    if (!proPrefix || isNaN(n)) return null;
    return proPrefix + String(n).padStart(3, "0");
  })();
  var embPreview = (function () {
    var n = parseInt(nextEmb, 10);
    if (!embPrefix || isNaN(n)) return null;
    return embPrefix + String(n).padStart(3, "0");
  })();

  return (
    <div>
      <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 14 }}>{t.invIntro}</div>

      {/* Invoice prefix */}
      <div style={{ maxWidth: 380 }}>
        <Field label={t.invPrefix}>
          <input value={prefix} onChange={function (e) { onPrefixChange(e.target.value); }} placeholder="GB-A-" style={{ ...inp, fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "var(--color-accent)" }} />
          <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 4, fontStyle: "italic" }}>{t.invPrefixHint}</div>
        </Field>
      </div>

      <div style={{ marginTop: 22 }}>
        <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-muted)", marginBottom: 6 }}>{t.invPreview}</div>
        {prefix ? (
          <div style={{ display: "flex", border: "1px solid var(--color-border)", borderRadius: 5, overflow: "hidden", maxWidth: 320 }}>
            <span style={{ padding: "8px 12px", background: "var(--color-bg)", color: "var(--color-text-muted)", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, borderRight: "1px solid var(--color-border)", whiteSpace: "nowrap" }}>{prefix}</span>
            <span style={{ flex: 1, padding: "8px 12px", fontFamily: "'JetBrains Mono', monospace", color: "var(--color-accent)", fontWeight: 600 }}>5062</span>
          </div>
        ) : (
          <div style={{ padding: "8px 12px", border: "1px dashed var(--color-border)", borderRadius: 5, maxWidth: 320, fontSize: 11, color: "var(--color-text-muted)", fontStyle: "italic" }}>
            {lang === "es" ? "Sin prefijo — el shipper captura el número completo." : "No prefix — the shipper enters the full number."}
          </div>
        )}
        <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 4, fontStyle: "italic" }}>{t.invPreviewHint}</div>
      </div>

      {/* Proforma numbering — separate sub-section under the same
          Invoicing tab. Two fields: prefix (text) and next number
          (integer). The portal increments the number after each
          new proforma; Arturo can reset/skip anytime from here. */}
      <div style={{ marginTop: 28, paddingTop: 20, borderTop: "1px solid var(--color-border)" }}>
        <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-accent)", marginBottom: 4 }}>{t.proSection}</div>
        <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginBottom: 14, fontStyle: "italic" }}>{t.proSectionHint}</div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, maxWidth: 540 }}>
          <Field label={t.proPrefix}>
            <input value={proPrefix} onChange={function (e) { onProPrefixChange(e.target.value); }} placeholder={shipperInitials ? shipperInitials + "-" : "GB-"} style={{ ...inp, fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "var(--color-accent)" }} />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 4 }}>
              <span style={{ fontSize: 10, color: "var(--color-text-muted)", fontStyle: "italic" }}>{t.proPrefixHint}</span>
              {shipperInitials && proPrefix !== shipperInitials + "-" && (
                <button type="button" onClick={applyInitialsToProPrefix} style={{ padding: "2px 8px", borderRadius: 4, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 10, fontWeight: 600 }}>{t.proResetToInitials}</button>
              )}
            </div>
          </Field>
          <Field label={t.proNextNumber}>
            <input type="text" inputMode="numeric" value={nextNum} onChange={function (e) { onNextNumChange(e.target.value); }} placeholder="1" style={{ ...inp, fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, textAlign: "center" }} />
            <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 4, fontStyle: "italic" }}>{t.proNextNumberHint}</div>
          </Field>
        </div>

        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-muted)", marginBottom: 6 }}>{t.proPreviewNext}</div>
          {proPreview ? (
            <div style={{ display: "inline-block", padding: "8px 14px", background: "rgba(42,122,111,0.08)", border: "1px solid rgba(42,122,111,0.25)", borderRadius: 6, fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "var(--color-accent)", fontSize: 14 }}>{proPreview}</div>
          ) : (
            <div style={{ padding: "8px 14px", border: "1px dashed var(--color-border)", borderRadius: 5, maxWidth: 320, fontSize: 11, color: "var(--color-text-muted)", fontStyle: "italic", display: "inline-block" }}>
              {lang === "es" ? "Configura prefijo + número para generar consecutivos automáticos." : "Set prefix + number to auto-generate consecutives."}
            </div>
          )}
        </div>

        {/* Warning banner — applies to both proforma and shipment
            sections; changing either counter affects the next number
            assigned by the portal. */}
        <div style={{ marginTop: 14, padding: "8px 12px", background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.3)", borderRadius: 5, fontSize: 11, color: "#B45309", fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flex: "0 0 auto" }}><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
          <span>{t.seqWarning}</span>
        </div>
      </div>

      {/* Shipment (embarque) numbering — same shape as proforma.
          Portal-side reserveEmbConsec reads these fields fresh
          from gfi-shipper-settings each time it runs. */}
      <div style={{ marginTop: 28, paddingTop: 20, borderTop: "1px solid var(--color-border)" }}>
        <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-accent)", marginBottom: 4 }}>{t.embSection}</div>
        <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginBottom: 14, fontStyle: "italic" }}>{t.embSectionHint}</div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, maxWidth: 540 }}>
          <Field label={t.embPrefix}>
            <input value={embPrefix} onChange={function (e) { onEmbPrefixChange(e.target.value); }} placeholder={shipperInitials ? shipperInitials + "-E" : "GB-E"} style={{ ...inp, fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "var(--color-accent)" }} />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 4 }}>
              <span style={{ fontSize: 10, color: "var(--color-text-muted)", fontStyle: "italic" }}>{t.embPrefixHint}</span>
              {shipperInitials && embPrefix !== shipperInitials + "-E" && (
                <button type="button" onClick={applyInitialsToEmbPrefix} style={{ padding: "2px 8px", borderRadius: 4, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 10, fontWeight: 600 }}>{t.embResetToInitials}</button>
              )}
            </div>
          </Field>
          <Field label={t.embNextNumber}>
            <input type="text" inputMode="numeric" value={nextEmb} onChange={function (e) { onNextEmbChange(e.target.value); }} placeholder="1" style={{ ...inp, fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, textAlign: "center" }} />
            <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 4, fontStyle: "italic" }}>{t.embNextNumberHint}</div>
          </Field>
        </div>

        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-muted)", marginBottom: 6 }}>{t.embPreviewNext}</div>
          {embPreview ? (
            <div style={{ display: "inline-block", padding: "8px 14px", background: "rgba(42,122,111,0.08)", border: "1px solid rgba(42,122,111,0.25)", borderRadius: 6, fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "var(--color-accent)", fontSize: 14 }}>{embPreview}</div>
          ) : (
            <div style={{ padding: "8px 14px", border: "1px dashed var(--color-border)", borderRadius: 5, maxWidth: 320, fontSize: 11, color: "var(--color-text-muted)", fontStyle: "italic", display: "inline-block" }}>
              {lang === "es" ? "Configura prefijo + número para generar consecutivos automáticos." : "Set prefix + number to auto-generate consecutives."}
            </div>
          )}
        </div>
      </div>

      {/* Save button lives in the parent header bar so it stays visible
          across all tabs and shows a combined dirty indicator. */}
    </div>
  );
}

/* =================== TAB: Coming Soon (shared) =================== */
/* ─── WarehousesTab — FIX 50 ───
   Mirrors AuthorizedProductsTab / BrokersTab. Checkbox-per-warehouse
   that toggles its presence in shipper_settings.assignedWarehouses.
   ShipperPortal filters its warehouse dropdown against this list. */
function WarehousesTab({ supplierId, lang, onToast, registerSave, onDirtyChange }) {
  var t = T[lang];
  var [warehouses, setWarehouses] = useState([]);
  var [assigned, setAssigned] = useState({});
  var [dirty, setDirty] = useState(false);

  useEffect(function () {
    var initial = loadLS("gfi-warehouses", []);
    setWarehouses(initial);
    /* Warm the supabaseDB cache so any future FK on warehouses
       resolves correctly. */
    loadFromSupabase("warehouses")
      .then(function (rows) { setWarehouses(rows || initial); })
      .catch(function () {});
    var all = loadAllSettings();
    var rec = all[supplierId] || {};
    var map = {};
    (rec.assignedWarehouses || []).forEach(function (id) { map[String(id)] = true; });
    setAssigned(map);
    setDirty(false);
  }, [supplierId]);

  useEffect(function () { if (onDirtyChange) onDirtyChange(dirty); }, [dirty, onDirtyChange]);
  useEffect(function () {
    if (!registerSave) return;
    var unregister = registerSave(function () { setDirty(false); });
    return unregister;
  }, [registerSave]);

  function persist(patch) {
    var all = loadAllSettings();
    if (!all[supplierId]) all[supplierId] = { ...DEFAULT_SHIPPER_SETTINGS };
    all[supplierId] = { ...all[supplierId], ...patch };
    saveLS(SETTINGS_KEY, all);
    pushShipperSettingsToSupabase(supplierId, all[supplierId]);
    setDirty(true);
  }
  function toggle(id) {
    var k = String(id);
    var next = { ...assigned };
    if (next[k]) delete next[k]; else next[k] = true;
    setAssigned(next);
    persist({ assignedWarehouses: Object.keys(next).filter(function (x) { return next[x]; }) });
  }

  var active = warehouses.filter(function (w) { return w.isActive !== false; });
  return (
    <div>
      <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginBottom: 14, fontStyle: "italic" }}>
        {lang === "es"
          ? "Marca las bodegas que este exportador podrá seleccionar al crear un embarque. Si dejas todo sin marcar, no se restringe."
          : "Tick the warehouses this shipper may pick when creating a shipment. Leave everything unticked to impose no restriction."}
      </div>
      {active.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--color-text-muted)", padding: 20, textAlign: "center", fontStyle: "italic" }}>{lang === "es" ? "No hay bodegas activas en el catálogo." : "No active warehouses in the catalog."}</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {active.map(function (w) {
            var on = !!assigned[String(w.id)];
            return (
              <label key={w.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 6, cursor: "pointer", background: on ? "var(--color-accent-bg)" : "var(--color-bg)", border: "1px solid " + (on ? "var(--color-accent)" : "var(--color-border)") }}>
                <input type="checkbox" checked={on} onChange={function () { toggle(w.id); }} style={{ accentColor: "var(--color-accent)" }} />
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <span style={{ fontWeight: 600, color: "var(--color-text)" }}>{w.shortName || w.fullName || "—"} {w.initials ? <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: "var(--color-text-muted)", marginLeft: 4 }}>({w.initials})</span> : null}</span>
                  {(w.city || w.address) && <span style={{ fontSize: 10, color: "var(--color-text-muted)" }}>{[w.city, w.state, w.address].filter(Boolean).join(" · ")}</span>}
                </div>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ComingSoonTab({ title, blurb, lang }) {
  var t = T[lang];
  return (
    <div style={{ padding: "60px 30px", textAlign: "center" }}>
      <svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" style={{ color: "var(--color-text-muted)", opacity: 0.6, marginBottom: 12 }}>
        <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
      </svg>
      <div style={{ fontSize: 16, fontWeight: 700, color: "var(--color-text)", marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 11, color: "var(--color-text-muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>{t.comingSoon}</div>
      <div style={{ fontSize: 12, color: "var(--color-text-secondary)", maxWidth: 380, margin: "0 auto", lineHeight: 1.5 }}>{blurb}</div>
    </div>
  );
}

/* =================== MAIN SHELL =================== */
export default function ShipperSettings({ lang: globalLang }) {
  var lang = globalLang || "es"; var t = T[lang];

  var [shippers, setShippers] = useState([]);
  var [selectedId, setSelectedId] = useState(null);
  var [activeTab, setActiveTab] = useState("company");
  var [search, setSearch] = useState("");
  var [toast, setToast] = useState(null);
  /* Bump after writing to gfi-shipper-settings so any external reads
     re-resolve. Not strictly required today but lets later tabs
     read fresh without prop drilling. */
  var [settingsBump, setSettingsBump] = useState(0);
  var [logoBusy, setLogoBusy] = useState(false);
  var logoInputRef = useRef(null);

  /* Aggregate dirty state + save registry across tabs. Each tab that
     has explicit save behavior calls registerSave(saveFn) on mount
     and onDirtyChange(true|false) when its local form mutates. The
     header "Save Changes" button reads these to enable/disable and to
     fan out a single click to every dirty tab's save. */
  var [tabsDirty, setTabsDirty] = useState({});
  var tabsSaveRef = useRef({});
  /* Memoize the per-tab bound handlers so identity is stable across
     renders — otherwise each render hands the child a fresh function
     reference and the registration useEffect fires every time, which
     can churn or loop. The keys here must match the tab keys used
     below in the JSX. */
  var tabHandlers = useMemo(function () {
    var keys = ["company", "invoicing", "products", "brokers", "warehouses"];
    var out = {};
    keys.forEach(function (key) {
      out[key] = {
        registerSave: function (saveFn) {
          tabsSaveRef.current[key] = saveFn;
          return function () {
            if (tabsSaveRef.current[key] === saveFn) delete tabsSaveRef.current[key];
          };
        },
        onDirtyChange: function (isDirty) {
          setTabsDirty(function (prev) {
            return prev[key] === isDirty ? prev : { ...prev, [key]: isDirty };
          });
        },
      };
    });
    return out;
  }, []);
  /* Reset dirty/registry when the selected shipper changes — the tabs
     unmount/remount so their stale entries shouldn't persist. */
  useEffect(function () {
    setTabsDirty({});
    tabsSaveRef.current = {};
  }, [selectedId]);
  var anyDirty = Object.keys(tabsDirty).some(function (k) { return !!tabsDirty[k]; });

  /* Shipper logo upload (immediate save). The logo lives on the supplier
     row (logoUrl/logoPath → suppliers.extra via supabaseDB passthrough)
     so the Shipper Portal can read it. The image goes to Supabase Storage
     (product-labels bucket, shipper-logos/ folder) — never base64 in the
     row. Timestamped path so a replaced logo gets a fresh URL (no stale
     browser cache); the previous object is deleted to avoid orphans.
     selectedShipper carries the full supplier object from the
     gfi-suppliers cache, so re-saving it with the two new fields keeps
     everything else intact. */
  var handleLogoFile = async function (e) {
    var file = e.target.files && e.target.files[0];
    if (e.target) e.target.value = "";
    if (!file || !selectedShipper) return;
    if (!/^image\//.test(file.type || "")) { setToast(lang === "es" ? "El logo debe ser una imagen." : "Logo must be an image."); return; }
    setLogoBusy(true);
    try {
      /* Read the file as a base64 data URL and upload via
         uploadDataUrlToStorage — the SAME path product label photos
         take (ProductRegistry uses uploadDataUrlToStorage with a root
         filename, and that upload is confirmed working). We mirror that
         flow exactly: same function, same bucket, root filename. Only
         the filename differs (shipper-logo-{id}), which the RLS policy
         does not restrict. */
      var dataUrl = await new Promise(function (resolve, reject) {
        var r = new FileReader();
        r.onload = function (ev) { resolve(ev.target.result); };
        r.onerror = function () { reject(new Error("read failed")); };
        r.readAsDataURL(file);
      });
      var up = await uploadDataUrlToStorage("product-labels", "shipper-logo-" + selectedShipper.id, dataUrl);
      var updated = Object.assign({}, selectedShipper, { logoPath: up.path, logoUrl: up.publicUrl });
      await saveToSupabase("suppliers", updated);
      setShippers(function (prev) { return prev.map(function (s) { return String(s.id) === String(selectedShipper.id) ? updated : s; }); });
      /* Also patch the gfi-suppliers localStorage cache so the focus-
         triggered pull() (which reloads shippers from cache when the OS
         file dialog hands focus back to the window) keeps the logoUrl
         instead of overwriting the preview with a pre-logo cache value. */
      try {
        var allSups = loadLS("gfi-suppliers", []) || [];
        var nextSups = allSups.map(function (s) { return String(s.id) === String(selectedShipper.id) ? Object.assign({}, s, { logoPath: up.path, logoUrl: up.publicUrl }) : s; });
        saveLS("gfi-suppliers", nextSups);
      } catch (cacheErr) { /* cache is best-effort */ }
      setToast(lang === "es" ? "Logo actualizado." : "Logo updated.");
    } catch (err) {
      setToast((lang === "es" ? "Error al subir el logo: " : "Logo upload failed: ") + (err && err.message ? err.message : String(err)));
    }
    setLogoBusy(false);
  };
  /* Elimina el logo del shipper — pone logoPath/logoUrl a null en la
     row del supplier y sincroniza cache local. Mismo save path que
     handleLogoFile pero sin upload. */
  var handleLogoRemove = async function () {
    if (!selectedShipper || logoBusy) return;
    var msg = lang === "es" ? "¿Eliminar el logo del shipper?" : "Remove shipper logo?";
    if (!window.confirm(msg)) return;
    setLogoBusy(true);
    try {
      var updated = Object.assign({}, selectedShipper, { logoPath: null, logoUrl: null });
      await saveToSupabase("suppliers", updated);
      setShippers(function (prev) { return prev.map(function (s) { return String(s.id) === String(selectedShipper.id) ? updated : s; }); });
      try {
        var allSups = loadLS("gfi-suppliers", []) || [];
        var nextSups = allSups.map(function (s) { return String(s.id) === String(selectedShipper.id) ? Object.assign({}, s, { logoPath: null, logoUrl: null }) : s; });
        saveLS("gfi-suppliers", nextSups);
      } catch (cacheErr) {}
      setToast(lang === "es" ? "Logo eliminado." : "Logo removed.");
    } catch (err) {
      setToast((lang === "es" ? "Error al eliminar el logo: " : "Logo remove failed: ") + (err && err.message ? err.message : String(err)));
    }
    setLogoBusy(false);
  };
  var doHeaderSave = function () {
    Object.keys(tabsDirty).forEach(function (key) {
      if (tabsDirty[key] && typeof tabsSaveRef.current[key] === "function") {
        tabsSaveRef.current[key]();
      }
    });
  };

  /* Load shippers on mount + when the tab regains focus, in case
     Arturo added a new supplier in another tab. */
  useEffect(function () {
    var pull = function () { setShippers(loadShippers()); };
    pull();
    var onFocus = function () { pull(); };
    window.addEventListener("focus", onFocus);
    return function () { window.removeEventListener("focus", onFocus); };
  }, []);

  /* Prime Supabase data on mount: pull suppliers (so pushShipperSettings
     can resolve the supplier_id uuid for the dual-write) and the
     shipper_settings rows (merged into localStorage as a base, so a
     fresh-device install hydrates from Supabase). Best-effort —
     failures fall back to whatever localStorage already had. */
  useEffect(function () {
    var alive = true;
    Promise.all([
      loadFromSupabase("suppliers").catch(function () { return []; }),
      loadFromSupabase("shipper_settings").catch(function () { return []; }),
    ]).then(function (results) {
      if (!alive) return;
      var rows = results[1] || [];
      if (rows.length === 0) return;
      try {
        var all = loadAllSettings();
        var changed = false;
        rows.forEach(function (r) {
          if (!r || !r.id) return;
          /* Merge Supabase row into localStorage object. Existing
             localStorage values win for fields the Supabase row has
             as undefined so an in-progress local edit isn't clobbered. */
          var existing = all[r.id] || {};
          var merged = { ...existing };
          Object.keys(r).forEach(function (k) {
            if (k === "id" || k === "_uuid" || k.startsWith("_")) return;
            if (r[k] !== undefined && r[k] !== null) merged[k] = r[k];
          });
          if (JSON.stringify(merged) !== JSON.stringify(existing)) {
            all[r.id] = merged; changed = true;
          }
        });
        if (changed) saveLS(SETTINGS_KEY, all);
      } catch (e) { /* swallow */ }
    });
    return function () { alive = false; };
  }, []);

  /* Auto-select the first shipper when shippers load and nothing
     is selected yet. */
  useEffect(function () {
    if (!selectedId && shippers.length > 0) setSelectedId(shippers[0].id);
  }, [shippers, selectedId]);

  /* When a shipper is selected for the first time, seed an empty
     settings entry for them so the storage record is consistent.
     Dual-write to Supabase so the row exists there too. */
  useEffect(function () {
    if (!selectedId) return;
    var all = loadAllSettings();
    if (!all[selectedId]) {
      all[selectedId] = { ...DEFAULT_SHIPPER_SETTINGS };
      saveLS(SETTINGS_KEY, all);
      pushShipperSettingsToSupabase(selectedId, all[selectedId]);
      setSettingsBump(function (x) { return x + 1; });
    }
  }, [selectedId]);

  var filteredShippers = useMemo(function () {
    if (!search) return shippers;
    var s = search.toLowerCase();
    return shippers.filter(function (sh) {
      return shipperDisplayName(sh).toLowerCase().includes(s)
        || (sh.initials || "").toLowerCase().includes(s);
    });
  }, [shippers, search]);

  var selectedShipper = useMemo(function () {
    return shippers.find(function (s) { return s.id === selectedId; }) || null;
  }, [shippers, selectedId]);

  var pushToast = useCallback(function (m) { setToast(m); }, []);

  /* Tab definitions — declarative so adding the future tabs is a
     one-liner each. */
  var TABS = [
    { key: "company", label: t.tabCompany },
    { key: "products", label: t.tabProducts },
    { key: "brokers", label: t.tabBrokers },
    { key: "warehouses", label: t.tabWarehouses },
    { key: "invoicing", label: t.tabInvoicing },
    { key: "permissions", label: t.tabPermissions },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 4px" }}>{t.title}</h2>
        <div style={{ fontSize: 12, color: "var(--color-text-muted)" }}>{t.subtitle}</div>
      </div>

      {shippers.length === 0 ? (
        <div style={{ padding: "40px 20px", textAlign: "center", color: "var(--color-text-muted)", fontSize: 13, border: "1px solid var(--color-border)", borderRadius: 10 }}>{t.noShippers}</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "260px 1fr", gap: 16, alignItems: "flex-start" }}>

          {/* LEFT: shippers list */}
          <div style={{ border: "1px solid var(--color-border)", borderRadius: 8, background: "var(--color-bg-card)", overflow: "hidden" }}>
            <div style={{ padding: "10px 12px", borderBottom: "1px solid var(--color-border)" }}>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-muted)", marginBottom: 6 }}>{t.selectShipper} · {shippers.length} {t.shippersCount}</div>
              <input value={search} onChange={function (e) { setSearch(e.target.value); }} placeholder={t.searchShippers} style={{ ...inp, fontSize: 11 }} />
            </div>
            <div style={{ maxHeight: 540, overflowY: "auto" }}>
              {filteredShippers.map(function (s) {
                var active = String(s.id) === String(selectedId);
                return (
                  <div key={s.id} onClick={function () { setSelectedId(s.id); }} style={{
                    padding: "10px 12px",
                    borderBottom: "1px solid var(--color-border)",
                    cursor: "pointer",
                    background: active ? "rgba(42,122,111,0.08)" : "transparent",
                    borderLeft: active ? "3px solid var(--color-accent)" : "3px solid transparent",
                  }} onMouseEnter={function (e) { if (!active) e.currentTarget.style.background = "var(--color-bg-hover, rgba(0,0,0,0.02))"; }} onMouseLeave={function (e) { if (!active) e.currentTarget.style.background = "transparent"; }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
                      <span style={{ fontWeight: active ? 700 : 600, fontSize: 12, color: active ? "var(--color-accent)" : "var(--color-text)" }}>{shipperDisplayName(s)}</span>
                      {s.initials && <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: "var(--color-text-muted)", padding: "1px 6px", border: "1px solid var(--color-border)", borderRadius: 4 }}>{s.initials}</span>}
                    </div>
                    {(s.city || s.country) && <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 2 }}>{[s.city, s.country].filter(Boolean).join(", ")}</div>}
                  </div>
                );
              })}
              {filteredShippers.length === 0 && <div style={{ padding: "16px 12px", color: "var(--color-text-muted)", fontSize: 11, textAlign: "center", fontStyle: "italic" }}>—</div>}
            </div>
          </div>

          {/* RIGHT: tabs + content */}
          <div style={{ border: "1px solid var(--color-border)", borderRadius: 8, background: "var(--color-bg-card)", minHeight: 540, maxHeight: "calc(100vh - 200px)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            {!selectedShipper ? (
              <div style={{ padding: "60px 30px", textAlign: "center", color: "var(--color-text-muted)", fontSize: 13 }}>{t.pickPrompt}</div>
            ) : (
              <>
                {/* Selected shipper header — Save Changes button on the
                    right stays visible across every tab. Enabled iff any
                    tab reported dirty state; click fans out to all
                    registered tab save functions. */}
                <div style={{ padding: "12px 18px", borderBottom: "1px solid var(--color-border)", display: "flex", justifyContent: "space-between", alignItems: "center", background: "var(--color-bg)" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ position: "relative", width: 52, height: 52, flexShrink: 0 }}>
                      <div onClick={function () { if (!logoBusy && logoInputRef.current) logoInputRef.current.click(); }} title={lang === "es" ? "Cambiar logo" : "Change logo"} style={{ width: 52, height: 52, borderRadius: 8, border: "1px dashed var(--color-border)", background: "var(--color-bg-card)", cursor: logoBusy ? "wait" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                        {selectedShipper.logoUrl
                          ? <img src={selectedShipper.logoUrl} alt="logo" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                          : <span style={{ fontSize: 9, color: "var(--color-text-muted)", textAlign: "center", padding: 4 }}>{logoBusy ? "…" : (lang === "es" ? "+ Logo" : "+ Logo")}</span>}
                      </div>
                      {selectedShipper.logoUrl && !logoBusy && (
                        <button onClick={function (e) { e.stopPropagation(); handleLogoRemove(); }} title={lang === "es" ? "Eliminar logo" : "Remove logo"} style={{ position: "absolute", top: -6, right: -6, width: 18, height: 18, borderRadius: "50%", border: "1px solid var(--color-border)", background: "#DC2626", color: "#fff", cursor: "pointer", fontSize: 11, fontWeight: 700, lineHeight: "16px", padding: 0, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }}>×</button>
                      )}
                    </div>
                    <input ref={logoInputRef} type="file" accept="image/*" onChange={handleLogoFile} style={{ display: "none" }} />
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "var(--color-text)" }}>{shipperDisplayName(selectedShipper)}</div>
                      <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 2 }}>
                        {selectedShipper.initials ? <span style={{ fontFamily: "'JetBrains Mono', monospace", marginRight: 8 }}>{selectedShipper.initials}</span> : null}
                        {selectedShipper.fullName && selectedShipper.fullName !== shipperDisplayName(selectedShipper) ? selectedShipper.fullName : ""}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={doHeaderSave}
                    disabled={!anyDirty}
                    style={{
                      padding: "8px 18px",
                      borderRadius: 6,
                      border: "none",
                      background: anyDirty ? "var(--color-accent)" : "#9ca3af",
                      color: "#fff",
                      cursor: anyDirty ? "pointer" : "default",
                      fontSize: 12,
                      fontWeight: 700,
                      opacity: anyDirty ? 1 : 0.55,
                      transition: "background 0.15s, opacity 0.15s",
                      whiteSpace: "nowrap",
                    }}
                    title={anyDirty ? "" : (lang === "es" ? "Sin cambios pendientes" : "No pending changes")}
                  >
                    {t.saveBtn}
                  </button>
                </div>

                {/* Tab bar */}
                <div style={{ display: "flex", gap: 0, borderBottom: "1px solid var(--color-border)", paddingLeft: 8 }}>
                  {TABS.map(function (tab) {
                    var active = activeTab === tab.key;
                    return (
                      <button key={tab.key} onClick={function () { setActiveTab(tab.key); }} style={{
                        padding: "10px 16px",
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                        fontSize: 12,
                        fontWeight: active ? 700 : 600,
                        color: active ? "var(--color-accent)" : "var(--color-text-secondary)",
                        borderBottom: active ? "2px solid var(--color-accent)" : "2px solid transparent",
                        marginBottom: -1,
                      }}>{tab.label}</button>
                    );
                  })}
                </div>

                {/* Tab content. Only tabs with explicit save behavior
                    receive registerSave/onDirtyChange — the others
                    auto-save on each change and don't participate in
                    the header button's dirty/save coordination. */}
                <div style={{ padding: "18px 22px", flex: 1, overflowY: "auto", maxHeight: "calc(100vh - 320px)" }}>
                  {activeTab === "company" && <CompanyInfoTab lang={lang} onToast={pushToast} registerSave={tabHandlers.company.registerSave} onDirtyChange={tabHandlers.company.onDirtyChange} />}
                  {activeTab === "products" && <AuthorizedProductsTab supplierId={selectedShipper.id} lang={lang} onToast={pushToast} registerSave={tabHandlers.products.registerSave} onDirtyChange={tabHandlers.products.onDirtyChange} />}
                  {activeTab === "brokers" && <BrokersTab supplierId={selectedShipper.id} lang={lang} onToast={pushToast} registerSave={tabHandlers.brokers.registerSave} onDirtyChange={tabHandlers.brokers.onDirtyChange} />}
                  {activeTab === "warehouses" && <WarehousesTab supplierId={selectedShipper.id} lang={lang} onToast={pushToast} registerSave={tabHandlers.warehouses.registerSave} onDirtyChange={tabHandlers.warehouses.onDirtyChange} />}
                  {activeTab === "invoicing" && <InvoicingTab supplierId={selectedShipper.id} lang={lang} onToast={pushToast} registerSave={tabHandlers.invoicing.registerSave} onDirtyChange={tabHandlers.invoicing.onDirtyChange} />}
                  {activeTab === "permissions" && <ComingSoonTab title={t.tabPermissions} blurb={t.comingSoonPermissions} lang={lang} />}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <Toast msg={toast} onDismiss={function () { setToast(null); }} />
      {/* Re-render hook for future tabs that read gfi-shipper-settings. */}
      <span style={{ display: "none" }}>{settingsBump}</span>
    </div>
  );
}
